import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Modal, Form, Input, Select, Button, Space, Tag, message, Popconfirm,
  InputNumber, Row, Col, Timeline, Dropdown, Spin, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import DraggableModal from '../../components/DraggableModal';
import {
  PlusOutlined, SearchOutlined, UploadOutlined, DownloadOutlined,
  DatabaseOutlined, DollarOutlined, AccountBookOutlined, ClockCircleOutlined, PlusCircleOutlined, HolderOutlined, SortAscendingOutlined, DeleteOutlined,
  CameraOutlined, EditOutlined, LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import api from '../../api';
import AssetPhotoPicker from '../../components/AssetPhotoPicker';
import {
  ASSET_PHOTO_MAX_COUNT,
  movePhotoInList,
  prepareAssetPhotoFile,
  setPhotoAsCover,
} from '../../utils/assetPhoto';

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), 'zh-Hans-CN', {
  numeric: true,
  sensitivity: 'base',
});
const SPECIAL_LAST_LOCATION_NAME = '3办公楼库房大连港专门办案场所边检处606室办公室6楼小木屋';
const compareLocationNameAsc = (a, b) => {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  const leftSpecial = left === SPECIAL_LAST_LOCATION_NAME;
  const rightSpecial = right === SPECIAL_LAST_LOCATION_NAME;
  if (leftSpecial && !rightSpecial) return 1;
  if (!leftSpecial && rightSpecial) return -1;
  const leftParts = left.split(/(\d+)/).filter(Boolean);
  const rightParts = right.split(/(\d+)/).filter(Boolean);
  const len = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < len; i += 1) {
    const lp = leftParts[i];
    const rp = rightParts[i];
    if (lp === undefined) return -1;
    if (rp === undefined) return 1;
    const lNum = /^\d+$/.test(lp);
    const rNum = /^\d+$/.test(rp);
    if (lNum && rNum) {
      const diff = Number(lp) - Number(rp);
      if (diff !== 0) return diff;
      continue;
    }
    if (lNum && !rNum) return -1;
    if (!lNum && rNum) return 1;
    const cmp = String(lp).localeCompare(String(rp), 'zh-Hans-CN', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
  }
  return 0;
};

const compareByRule = (left, right, field, order) => {
  let result = 0;
  if (field === 'original_value' || field === 'net_value' || field === 'asset_status') {
    result = Number(left?.[field] ?? 0) - Number(right?.[field] ?? 0);
  } else if (field === 'create_time') {
    result = new Date(left?.create_time || 0).getTime() - new Date(right?.create_time || 0).getTime();
  } else if (field === 'location_name') {
    result = compareLocationNameAsc(left?.location_name, right?.location_name);
  } else {
    result = compareText(left?.[field], right?.[field]);
  }
  return order === 'desc' ? -result : result;
};

const sortRowsByRules = (rows, rules) => {
  const validRules = (rules || []).filter(rule => rule?.field && rule?.order);
  if (!validRules.length) return rows;
  return [...rows].sort((a, b) => {
    for (let i = 0; i < validRules.length; i += 1) {
      const { field, order } = validRules[i];
      const compared = compareByRule(a, b, field, order);
      if (compared !== 0) return compared;
    }
    return 0;
  });
};

export default function Assets() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [stats, setStats] = useState({});
  const [locations, setLocations] = useState([]);
  const [assetUsers, setAssetUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailSnapshot, setDetailSnapshot] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logAssetName, setLogAssetName] = useState('');
  const [form] = Form.useForm();
  const [deleteFilterForm] = Form.useForm();
  const importInputRef = useRef(null);
  const photoReplaceInputRef = useRef(null);
  const replacePhotoIdRef = useRef(null);
  const [deleteFilterModalOpen, setDeleteFilterModalOpen] = useState(false);
  const [editingPhotos, setEditingPhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);

  // Search filters
  const [keyword, setKeyword] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [locationFilter, setLocationFilter] = useState([]); // 多选
  const [userFilter, setUserFilter] = useState([]); // 多选
  const [netValueMin, setNetValueMin] = useState(undefined);
  const [netValueMax, setNetValueMax] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // 表示用户是“全选本页/全选全部资产/自定义勾选”
  const [assetSelectAllMode, setAssetSelectAllMode] = useState('none'); // none | page | all | custom
  const [batchChangeOpen, setBatchChangeOpen] = useState(false);
  const [batchChangeScope, setBatchChangeScope] = useState('page'); // selected | page | all
  const [batchNewLocationId, setBatchNewLocationId] = useState(null);
  const [batchNewUserId, setBatchNewUserId] = useState(null);
  const [batchChangeLoading, setBatchChangeLoading] = useState(false);
  const defaultSortRules = [
    { field: 'location_name', order: 'asc' },
    { field: 'asset_name', order: 'asc' },
    { field: 'user_name', order: 'asc' },
    { field: 'asset_code', order: 'asc' },
  ];
  const [sortRules, setSortRules] = useState(defaultSortRules);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [draftSortRules, setDraftSortRules] = useState([]);
  const [draggingSortIndex, setDraggingSortIndex] = useState(null);
  const [opLogOpen, setOpLogOpen] = useState(false);
  const [opLogs, setOpLogs] = useState([]);
  const [opLogLoading, setOpLogLoading] = useState(false);
  const [opLogSelected, setOpLogSelected] = useState([]);
  const [ioDialog, setIoDialog] = useState({
    open: false, loading: false, title: '', lines: [],
  });

  const sortFieldOptions = [
    { value: 'location_name', label: '存放位置' },
    { value: 'asset_name', label: '资产名称' },
    { value: 'asset_code', label: '资产编号' },
    { value: 'quantity', label: '数量' },
    { value: 'user_name', label: '使用人' },
    { value: 'original_value', label: '原值' },
    { value: 'net_value', label: '净值' },
    { value: 'asset_status', label: '折旧状态' },
    { value: 'create_time', label: '创建时间' },
  ];

  const buildQueryParams = useCallback((page, pageSize, rulesOverride) => {
    const rules = rulesOverride !== undefined ? rulesOverride : sortRules;
    const params = { page, page_size: pageSize };
    if (keyword) params.keyword = keyword;
    if (categoryFilter) params.asset_type = categoryFilter;
    if (statusFilter !== undefined && statusFilter !== null) params.asset_status = statusFilter;
    if (Array.isArray(locationFilter) && locationFilter.length) params.location_ids = locationFilter;
    if (Array.isArray(userFilter) && userFilter.length) params.user_ids = userFilter;
    if (netValueMin !== undefined && netValueMin !== null) params.net_value_min = netValueMin;
    if (netValueMax !== undefined && netValueMax !== null) params.net_value_max = netValueMax;
    const validSort = rules
      .filter(rule => rule?.field && rule?.order)
      .map(rule => `${rule.field}:${rule.order}`);
    if (validSort.length) params.sort_by = validSort.join(',');
    return params;
  }, [
    sortRules,
    keyword,
    categoryFilter,
    statusFilter,
    locationFilter,
    userFilter,
    netValueMin,
    netValueMax,
  ]);

  const fetchData = useCallback(async (page = 1, pageSize = 10, rulesOverride) => {
    setLoading(true);
    try {
      const params = buildQueryParams(page, pageSize, rulesOverride);
      const res = await api.get('/assets', { params });
      const rows = Array.isArray(res?.items) ? res.items : [];
      setData(sortRowsByRules(rows, params.sort_by ? rulesOverride || sortRules : sortRules));
      setPagination(p => ({ ...p, current: page, pageSize, total: res?.total || 0 }));
      setSelectedRowKeys([]);
    } catch {
      message.error('获取资产列表失败');
    }
    setLoading(false);
  }, [buildQueryParams, sortRules]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res || {});
    } catch { /* ignore */ }
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const [locRes, userRes] = await Promise.all([
        api.get('/asset-locations'),
        api.get('/asset-users'),
      ]);
      setLocations(Array.isArray(locRes) ? locRes : []);
      setAssetUsers(Array.isArray(userRes) ? userRes : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
    fetchOptions();
  }, [fetchData, fetchStats, fetchOptions]);

  const depreciatingCount = Number(stats.depreciating_assets ?? 0);
  const completedDepreciationCount = Number(stats.completed_assets ?? 0);

  // Collect unique asset_type values for category filter
  const categoryOptions = [...new Set(data.map(d => d.asset_type).filter(Boolean))].map(t => ({
    value: t, label: t,
  }));

  const handleSearch = () => fetchData(1, pagination.pageSize);

  const openSortModal = () => {
    setDraftSortRules([]);
    setSortModalOpen(true);
  };

  const applySortRules = () => {
    const cleaned = draftSortRules.filter(r => r.field && r.order);
    const fields = cleaned.map(r => r.field);
    if (new Set(fields).size !== fields.length) {
      message.warning('排序字段不能重复，请调整后再应用');
      return;
    }
    const next = cleaned.length ? cleaned : [...defaultSortRules];
    setSortRules(next);
    setSortModalOpen(false);
    fetchData(1, pagination.pageSize, next);
  };

  const restoreDefaultSortRules = () => {
    const next = [...defaultSortRules];
    setSortRules(next);
    setDraftSortRules([]);
    setSortModalOpen(false);
    fetchData(1, pagination.pageSize, next);
  };

  const handleCreate = () => {
    setEditing(null);
    setDetailSnapshot(null);
    setDetailEditMode(true);
    setEditingPhotos([]);
    form.resetFields();
    setModalOpen(true);
  };

  const loadAssetDetail = useCallback(async (assetId) => {
    const res = await api.get(`/assets/${assetId}`);
    const detail = res?.data ?? res;
    setDetailSnapshot(detail);
    form.setFieldsValue({
      ...detail,
      accounting_date: detail.accounting_date ? dayjs(detail.accounting_date) : null,
      acquire_date: detail.acquire_date ? dayjs(detail.acquire_date) : null,
    });
    return detail;
  }, [form]);

  const loadAssetPhotos = useCallback(async (assetId) => {
    if (!assetId) return;
    try {
      const res = await api.get(`/assets/${assetId}`);
      setEditingPhotos(Array.isArray(res?.photos) ? res.photos : []);
    } catch {
      setEditingPhotos([]);
    }
  }, []);

  const handleEdit = async (record) => {
    setEditing(record);
    setDetailSnapshot(null);
    setDetailEditMode(false);
    setModalOpen(true);
    setDetailLoading(true);
    try {
      const detail = await loadAssetDetail(record.id);
      setEditing(detail);
      await loadAssetPhotos(record.id);
    } catch (err) {
      message.error(err.message || '加载资产详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const uploadEditingPhoto = async (rawFile) => {
    if (!editing?.id || !rawFile) return;
    if (editingPhotos.length >= ASSET_PHOTO_MAX_COUNT) {
      message.warning(`每个资产最多 ${ASSET_PHOTO_MAX_COUNT} 张照片`);
      return;
    }
    setPhotoBusy(true);
    try {
      const file = await prepareAssetPhotoFile(rawFile);
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/assets/${editing.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAssetPhotos(editing.id);
      message.success('照片已上传并设为封面，可用左右箭头调整顺序');
    } catch (err) {
      message.error(err.message || '上传失败');
    } finally {
      setPhotoBusy(false);
    }
  };

  const replaceEditingPhoto = async (photoId, rawFile) => {
    if (!editing?.id || !photoId || !rawFile) return;
    setPhotoBusy(true);
    try {
      const file = await prepareAssetPhotoFile(rawFile);
      const fd = new FormData();
      fd.append('file', file);
      await api.put(`/assets/${editing.id}/photos/${photoId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAssetPhotos(editing.id);
      message.success('照片替换成功');
    } catch (err) {
      message.error(err.message || '替换失败');
    } finally {
      setPhotoBusy(false);
    }
  };

  const deleteEditingPhoto = async (photoId) => {
    if (!editing?.id || !photoId) return;
    setPhotoBusy(true);
    try {
      await api.delete(`/assets/${editing.id}/photos/${photoId}`);
      await loadAssetPhotos(editing.id);
      message.success('照片删除成功');
    } catch (err) {
      message.error(err.message || '删除失败');
    } finally {
      setPhotoBusy(false);
    }
  };

  const reorderEditingPhotos = async (nextPhotos) => {
    if (!editing?.id) return;
    const prev = editingPhotos;
    setEditingPhotos(nextPhotos);
    setPhotoBusy(true);
    try {
      await api.put(`/assets/${editing.id}/photos/order`, { photo_ids: nextPhotos.map((p) => p.id) });
      await loadAssetPhotos(editing.id);
      message.success('封面/顺序已更新');
    } catch (err) {
      setEditingPhotos(prev);
      message.error(err.message || '排序失败');
    } finally {
      setPhotoBusy(false);
    }
  };

  const moveEditingPhoto = (photoId, delta) => {
    const idx = editingPhotos.findIndex((p) => p.id === photoId);
    if (idx < 0) return;
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= editingPhotos.length) return;
    reorderEditingPhotos(movePhotoInList(editingPhotos, photoId, nextIdx));
  };

  const makeEditingCover = (photoId) => {
    if (!editingPhotos.length || editingPhotos[0]?.id === photoId) return;
    reorderEditingPhotos(setPhotoAsCover(editingPhotos, photoId));
  };

  const handleSubmit = async () => {
    try {
      if (editing && !detailEditMode) {
        message.info('请先点击“编辑”后再保存');
        return;
      }
      const values = await form.validateFields();
      const payload = {
        ...values,
        accounting_date: values.accounting_date
          ? values.accounting_date.format('YYYY-MM-DD')
          : null,
        acquire_date: values.acquire_date
          ? values.acquire_date.format('YYYY-MM-DD')
          : null,
      };
      if (editing) {
        Modal.confirm({
          title: '确认保存修改？',
          content: '保存后将更新该资产信息。',
          okText: '确认保存',
          cancelText: '取消',
          onOk: async () => {
            try {
              await api.put(`/assets/${editing.id}`, payload);
              message.success('更新成功');
              setModalOpen(false);
              fetchData(pagination.current, pagination.pageSize);
              fetchStats();
            } catch (err) {
              message.error(err.message || '更新失败');
            }
          },
        });
        return;
      } else {
        await api.post('/assets', payload);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch { /* validation or api error */ }
  };

  const handleExport = async (scope = 'all') => {
    setIoDialog({
      open: true,
      loading: true,
      title: scope === 'current' ? '正在导出当前筛选数据' : '正在导出全部资产',
      lines: ['请稍候，导出完成前请勿重复提交。'],
    });
    try {
      const token = localStorage.getItem('token');
      const query = new URLSearchParams();
      // Send multiple compatible scope params so older backends still work.
      query.set('export_scope', scope);
      query.set('scope', scope);
      query.set('export_type', scope);
      if (scope === 'current') {
        if (keyword) query.set('keyword', String(keyword));
        if (categoryFilter) query.set('asset_type', String(categoryFilter));
        if (statusFilter !== undefined && statusFilter !== null) query.set('asset_status', String(statusFilter));
        if (Array.isArray(locationFilter) && locationFilter.length) {
          locationFilter.forEach((id) => {
            query.append('location_ids', String(id));
            // 兼容后端可能采用的 bracket 形式
            query.append('location_ids[]', String(id));
          });
        }
        if (Array.isArray(userFilter) && userFilter.length) {
          userFilter.forEach((id) => {
            query.append('user_ids', String(id));
            query.append('user_ids[]', String(id));
          });
        }
        if (netValueMin !== undefined && netValueMin !== null) query.set('net_value_min', String(netValueMin));
        if (netValueMax !== undefined && netValueMax !== null) query.set('net_value_max', String(netValueMax));
        if (sortRules?.length) {
          query.set('sort_by', sortRules.map(rule => `${rule.field}:${rule.order}`).join(','));
        }
      }
      const r = await fetch(`${window.location.origin}/api/assets/excel/export?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('导出失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = scope === 'current'
        ? `assets_export_current_${Date.now()}.xlsx`
        : `assets_export_all_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      const exportedCount = scope === 'current'
        ? Number(total || 0)
        : Number(stats.total_assets || 0);
      setIoDialog({
        open: true,
        loading: false,
        title: '导出完成',
        lines: [
          `成功数：${exportedCount}`,
          '更新数：0',
          '跳过数：0',
        ],
      });
    } catch {
      setIoDialog({
        open: true,
        loading: false,
        title: '导出失败',
        lines: ['导出过程中发生错误，请重试。'],
      });
    }
  };

  const handleImportClick = () => importInputRef.current?.click();

  const handleDownloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/assets/excel/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('下载失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '资产导入模板.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('已下载资产导入模板（含单价、入账日期、取得日期；存放地点/使用人下拉与当前库同步）');
    } catch (err) {
      message.error(err.message || '下载模板失败');
    }
  };

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setIoDialog({
      open: true,
      loading: true,
      title: '正在导入资产',
      lines: ['请稍候，导入完成前请勿重复提交。'],
    });
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${window.location.origin}/api/assets/excel/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const raw = await r.text();
      let j = null;
      try {
        j = raw ? JSON.parse(raw) : null;
      } catch {
        // 后端在异常场景下可能返回纯文本，避免直接 JSON.parse 导致前端报错信息不可读
      }
      if (!r.ok) {
        throw new Error(j?.msg || raw || `导入失败（HTTP ${r.status}）`);
      }
      if (!j || j.code !== 200) {
        throw new Error(j?.msg || raw || '导入失败');
      }
      const {
        created = 0,
        updated = 0,
        errors = [],
        ignored_column_count = 0,
        ignored_columns = [],
      } = j.data || {};
      const errList = Array.isArray(errors) ? errors.map(String) : [];
      const maxShow = 20;
      const lines = [
        `新增：${Number(created || 0)}`,
        `更新：${Number(updated || 0)}`,
        `失败/跳过：${errList.length}`,
      ];
      if (Number(ignored_column_count) > 0) {
        const sample = (Array.isArray(ignored_columns) ? ignored_columns : [])
          .slice(0, 6)
          .join('、');
        lines.push(
          `已忽略多余列 ${Number(ignored_column_count)} 个（按表头自动识别；如产权、使用面积等系统未用字段不影响导入）`
          + (sample ? `：${sample}${Number(ignored_column_count) > 6 ? '…' : ''}` : '')
        );
      }
      if (errList.length) {
        lines.push('—— 失败明细 ——');
        lines.push(...errList.slice(0, maxShow));
        if (errList.length > maxShow) {
          lines.push(`……其余 ${errList.length - maxShow} 条已省略，可在浏览器控制台查看全部`);
        }
        console.warn('资产导入失败明细', errList);
      }
      setIoDialog({
        open: true,
        loading: false,
        title: errList.length ? '导入完成（含失败项）' : '导入完成',
        lines,
      });
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (err) {
      setIoDialog({
        open: true,
        loading: false,
        title: '导入失败',
        lines: [err.message || '导入失败，请重试。'],
      });
    }
  };

  const handleViewLogs = async (record) => {
    try {
      const res = await api.get(`/assets/${record.id}`);
      setLogs(res?.logs || []);
      setLogAssetName(record.asset_name || '');
      setLogModalOpen(true);
    } catch {
      message.error('获取日志失败');
    }
  };

  const fetchOpLogs = async () => {
    setOpLogLoading(true);
    try {
      const res = await api.get('/data-op-logs', { params: { module: 'assets', page: 1, page_size: 200 } });
      setOpLogs(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      message.error(err.message || '获取操作日志失败');
    }
    setOpLogLoading(false);
  };

  const openOpLogs = () => {
    setOpLogSelected([]);
    setOpLogOpen(true);
    fetchOpLogs();
  };

  const exportOneOpLog = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/data-op-logs/${id}/export-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('导出失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_op_log_${id}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (err) {
      message.error(err.message || '导出失败');
    }
  };

  const exportSelectedOpLogs = async () => {
    if (!opLogSelected.length) {
      message.warning('请先勾选日志记录');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/data-op-logs/export-selected-details`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ log_ids: opLogSelected }),
      });
      if (!r.ok) throw new Error('导出失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_op_logs_selected_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (err) {
      message.error(err.message || '导出失败');
    }
  };

  const buildFilteredDeletePayload = (values = {}) => {
    const payload = { scope: 'filtered' };
    if (values.keyword) payload.keyword = values.keyword;
    if (values.asset_type) payload.asset_type = values.asset_type;
    if (values.asset_status !== undefined && values.asset_status !== null) payload.asset_status = values.asset_status;
    if (Array.isArray(values.location_ids) && values.location_ids.length) payload.location_ids = values.location_ids;
    if (Array.isArray(values.user_ids) && values.user_ids.length) payload.user_ids = values.user_ids;
    if (values.net_value_min !== undefined && values.net_value_min !== null) payload.net_value_min = values.net_value_min;
    if (values.net_value_max !== undefined && values.net_value_max !== null) payload.net_value_max = values.net_value_max;
    return payload;
  };

  const getCurrentPageAssetIds = () => (Array.isArray(data) ? data.map(d => d.id) : []);

  const fetchAllAssetIds = async () => {
    const pageSize = 100; // 后端约束：<=100
    let page = 1;
    const all = [];
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const params = buildQueryParams(page, pageSize, sortRules);
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get('/assets', { params });
      const items = Array.isArray(res?.items) ? res.items : [];
      all.push(...items.map(it => it.id));
      const total = Number(res?.total || 0);
      if (!total || all.length >= total || !items.length) break;
      page += 1;
    }
    return all;
  };

  const openBatchChangeModal = () => {
    let defaultScope = 'page';
    if (assetSelectAllMode === 'all') defaultScope = 'all';
    else if (assetSelectAllMode === 'custom') defaultScope = 'selected';
    setBatchChangeScope(defaultScope);
    setBatchNewLocationId(null);
    setBatchNewUserId(null);
    setBatchChangeOpen(true);
  };

  const applyBatchChange = async () => {
    let targetIds = [];
    if (batchChangeScope === 'selected') {
      targetIds = [...selectedRowKeys];
    } else if (batchChangeScope === 'page') {
      targetIds = getCurrentPageAssetIds();
    } else {
      targetIds = await fetchAllAssetIds();
    }
    if (!targetIds.length) {
      message.warning(batchChangeScope === 'selected' ? '请先勾选要变更的资产' : '未找到需要变更的资产');
      return;
    }
    if (!batchNewLocationId && !batchNewUserId) {
      message.warning('请选择新存放地点或新使用人');
      return;
    }

    setBatchChangeLoading(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const id of targetIds) {
        const payload = {};
        if (batchNewLocationId) payload.new_location_id = batchNewLocationId;
        if (batchNewUserId) payload.new_user_id = batchNewUserId;
        // eslint-disable-next-line no-await-in-loop
        await api.put(`/assets/${id}/change`, payload);
      }
      message.success('批量变更成功');
      setBatchChangeOpen(false);
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (err) {
      message.error(err.message || '批量变更失败');
    } finally {
      setBatchChangeLoading(false);
    }
  };

  const buildFilterSummary = (values = {}) => {
    const parts = [];
    if (values.keyword) parts.push(`关键词: ${values.keyword}`);
    if (values.asset_type) parts.push(`类别: ${values.asset_type}`);
    if (values.asset_status !== undefined && values.asset_status !== null) {
      parts.push(`折旧状态: ${values.asset_status === 0 ? '折旧中' : '已完成'}`);
    }
    if (Array.isArray(values.location_ids) && values.location_ids.length) {
      const names = values.location_ids.map((id) => {
        const loc = locations.find(l => l.id === id);
        return loc?.location_name || String(id);
      });
      parts.push(`存放位置: ${names.join('、')}`);
    }
    if (Array.isArray(values.user_ids) && values.user_ids.length) {
      const names = values.user_ids.map((id) => {
        const u = assetUsers.find(item => item.id === id);
        return u?.username || String(id);
      });
      parts.push(`使用人: ${names.join('、')}`);
    }
    if (values.net_value_min !== undefined && values.net_value_min !== null) parts.push(`净值最小: ${values.net_value_min}`);
    if (values.net_value_max !== undefined && values.net_value_max !== null) parts.push(`净值最大: ${values.net_value_max}`);
    return parts.length ? parts.join('；') : '未设置筛选条件（将删除全部资产）';
  };

  const doBatchDelete = async (payload, successText) => {
    const res = await api.post('/assets/delete-batch', payload);
    message.success(`${successText}${res?.deleted_count != null ? `（共 ${res.deleted_count} 条）` : ''}`);
    fetchData(pagination.current, pagination.pageSize);
    fetchStats();
  };

  const handleDeleteOne = async (record) => {
    try {
      await api.delete(`/assets/${record.id}`);
      message.success('删除成功');
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的资产');
      return;
    }
    try {
      if (assetSelectAllMode === 'all') {
        const payload = buildFilteredDeletePayload({
          keyword,
          asset_type: categoryFilter,
          asset_status: statusFilter,
          location_ids: locationFilter,
          user_ids: userFilter,
          net_value_min: netValueMin,
          net_value_max: netValueMax,
        });
        await doBatchDelete(payload, '删除全部资产成功');
      } else {
        await doBatchDelete({ scope: 'ids', ids: selectedRowKeys }, '批量删除成功');
      }
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const openDeleteFilteredModal = () => {
    deleteFilterForm.resetFields();
    setDeleteFilterModalOpen(true);
  };

  const handleDeleteFiltered = async () => {
    try {
      const values = await deleteFilterForm.validateFields();
      const hasAnyFilter = [
        values.keyword,
        values.asset_type,
        values.asset_status,
        values.location_ids,
        values.user_ids,
        values.net_value_min,
        values.net_value_max,
      ].some(v => v !== undefined && v !== null && v !== '');
      if (!hasAnyFilter) {
        message.warning('请至少填写一项筛选条件，禁止空白删除');
        return;
      }
      await doBatchDelete(buildFilteredDeletePayload(values), `按筛选条件删除成功（${buildFilterSummary(values)}）`);
      setDeleteFilterModalOpen(false);
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const formatMoney = (val) => {
    if (val == null) return '¥0';
    return `¥${Number(val).toLocaleString()}`;
  };

  const statCards = [
    {
      label: '总资产',
      value: stats.total_assets != null ? Number(stats.total_assets).toLocaleString() : '0',
      icon: <DatabaseOutlined style={{ fontSize: 22, color: '#1890ff' }} />,
      bgColor: '#e6f7ff',
    },
    {
      label: '总原值',
      value: formatMoney(stats.total_original_value),
      icon: <DollarOutlined style={{ fontSize: 22, color: '#fa8c16' }} />,
      bgColor: '#fff7e6',
    },
    {
      label: '总净值',
      value: formatMoney(stats.total_net_value),
      icon: <AccountBookOutlined style={{ fontSize: 22, color: '#52c41a' }} />,
      bgColor: '#f6ffed',
    },
    {
      label: '折旧中资产',
      value: depreciatingCount.toLocaleString(),
      icon: <ClockCircleOutlined style={{ fontSize: 22, color: '#13c2c2' }} />,
      bgColor: '#e6fffb',
    },
    {
      label: '已完成折旧资产',
      value: completedDepreciationCount.toLocaleString(),
      icon: <ClockCircleOutlined style={{ fontSize: 22, color: '#722ed1' }} />,
      bgColor: '#f9f0ff',
    },
  ];

  const columns = [
    {
      title: '资产信息', dataIndex: 'asset_name', key: 'asset_info', width: 180,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.asset_type}</div>
        </div>
      ),
    },
    {
      title: '照片', key: 'photo', width: 110,
      render: (_, record) => (
        record.cover_photo_url ? (
          <img
            src={record.cover_photo_url}
            alt={record.asset_name || '资产照片'}
            style={{ width: 72, height: 42, objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }}
          />
        ) : (
          <span style={{ color: '#bbb' }}>暂无</span>
        )
      ),
    },
    { title: '资产编号', dataIndex: 'asset_code', key: 'asset_code', width: 128 },
    {
      title: '数量', dataIndex: 'quantity', key: 'quantity', width: 65,
      render: (v) => (v == null ? 0 : v),
    },
    {
      title: '资产原值', dataIndex: 'original_value', key: 'original_value', width: 104,
      render: v => (v != null ? `¥${Number(v).toLocaleString()}` : '-'),
    },
    {
      title: '资产净值', dataIndex: 'net_value', key: 'net_value', width: 104,
      render: v => (v != null ? `¥${Number(v).toLocaleString()}` : '-'),
    },
    { title: '存放位置', dataIndex: 'location_name', key: 'location_name', width: 120 },
    {
      title: '使用人', key: 'user', width: 108,
      render: (_, record) => (
        <div style={{ lineHeight: 1.3 }}>
          <div>{record.user_name || '-'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.user_work_no || '-'}</div>
        </div>
      ),
    },
    {
      title: '折旧状态', key: 'asset_status', width: 112,
      render: (_, record) => (Number(record.net_value) <= 0
        ? <Tag color="green">折旧完成</Tag>
        : <Tag color="blue">折旧中</Tag>),
    },
    {
      title: '操作', key: 'actions', width: 120, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleEdit(record)}>详情</Button>
          <Popconfirm
            title="确认删除该资产？"
            description="删除后将从资产列表移除。"
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleDeleteOne(record)}
          >
            <Button danger type="link" size="small" style={{ padding: 0 }}>删除</Button>
          </Popconfirm>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleViewLogs(record)}>日志</Button>
        </Space>
      ),
    },
  ];

  const { current, pageSize, total } = pagination;

  return (
    <div style={{ padding: 24, background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>资产管理</h1>
        <Space>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button icon={<UploadOutlined />} onClick={handleImportClick}>导入资产</Button>
          <Button onClick={handleDownloadImportTemplate}>下载导入模板</Button>
          <Dropdown
            menu={{
              items: [
                { key: 'current', label: '导出当前筛选项' },
                { key: 'all', label: '导出全部资产' },
              ],
              onClick: ({ key }) => handleExport(key),
            }}
          >
            <Button icon={<DownloadOutlined />}>导出资产</Button>
          </Dropdown>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增资产</Button>
          <Button onClick={openOpLogs}>操作日志</Button>
        </Space>
      </div>

      {/* Stat Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        {statCards.map((card) => (
          <Col flex="1" key={card.label}>
            <div style={{
              background: '#fff', borderRadius: 8, padding: 20,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 120,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: card.bgColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {card.icon}
              </div>
              <div style={{ textAlign: 'center', width: '100%' }}>
                <div style={{ fontSize: 30, fontWeight: 'bold', lineHeight: 1.2 }}>{card.value}</div>
                <div style={{ fontSize: 14, color: '#999', marginTop: 2 }}>{card.label}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Search Area */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col flex="2">
            <Input
              placeholder="搜索资产名称/编号/清查编号"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col flex="1">
            <Select
              placeholder="全部类别"
              allowClear
              value={categoryFilter}
              onChange={v => setCategoryFilter(v)}
              style={{ width: '100%' }}
              options={categoryOptions}
            />
          </Col>
          <Col flex="1">
            <Select
              placeholder="折旧状态"
              allowClear
              value={statusFilter}
              onChange={v => setStatusFilter(v)}
              style={{ width: '100%' }}
              options={[
                { value: 0, label: '折旧中' },
                { value: 1, label: '已完成' },
              ]}
            />
          </Col>
          <Col>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col flex="1">
            <Select
              placeholder="存放位置"
              allowClear
              showSearch
              optionFilterProp="label"
              mode="multiple"
              value={locationFilter}
              onChange={v => setLocationFilter(v)}
              style={{ width: '100%' }}
              options={(Array.isArray(locations) ? locations : []).map(l => ({ value: l.id, label: l.location_name }))}
            />
          </Col>
          <Col flex="1">
            <Select
              placeholder="使用人"
              allowClear
              showSearch
              optionFilterProp="label"
              mode="multiple"
              value={userFilter}
              onChange={v => setUserFilter(v)}
              style={{ width: '100%' }}
              options={(Array.isArray(assetUsers) ? assetUsers : []).map(u => ({ value: u.id, label: u.username }))}
            />
          </Col>
          <Col flex="1">
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                placeholder="最小净值"
                value={netValueMin}
                onChange={v => setNetValueMin(v)}
                min={0}
                style={{ width: '50%' }}
              />
              <InputNumber
                placeholder="最大净值"
                value={netValueMax}
                onChange={v => setNetValueMax(v)}
                min={0}
                style={{ width: '50%' }}
              />
            </Space.Compact>
          </Col>
        </Row>
      </div>

      {/* Stats text */}
      <div style={{ marginBottom: 12, fontSize: 14, color: '#666' }}>
        共 <strong>{total}</strong> 条资产
        <Button
          style={{ marginLeft: 12 }}
          icon={<SortAscendingOutlined />}
          onClick={openSortModal}
        >
          自定义排序
        </Button>
        <Popconfirm
          title={`仅删除已勾选项：确认删除 ${assetSelectAllMode === 'all' ? total : selectedRowKeys.length} 条资产？`}
          description="此操作只影响当前勾选记录，不会删除其它筛选结果。"
          okText="删除"
          cancelText="取消"
          onConfirm={handleDeleteSelected}
          disabled={!selectedRowKeys.length}
        >
          <Button
            style={{ marginLeft: 12 }}
            danger
            icon={<DeleteOutlined />}
            disabled={!selectedRowKeys.length}
          >
            删除已勾选
          </Button>
        </Popconfirm>
        <Button style={{ marginLeft: 8 }} danger onClick={openDeleteFilteredModal}>
          按筛选条件删除
        </Button>
        <Button
          style={{ marginLeft: 8 }}
          onClick={openBatchChangeModal}
        >
          一键修改存放地点/使用人
        </Button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8 }}>
        <Table
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => {
              setSelectedRowKeys(keys);
              const pageIds = getCurrentPageAssetIds();
              if (!keys.length) {
                setAssetSelectAllMode('none');
                return;
              }
              if (assetSelectAllMode === 'all') {
                // “全选全部资产”在当前页只显示本页复选框；用户手动取消部分，则视为自定义
                if (keys.length !== pageIds.length) setAssetSelectAllMode('custom');
                return;
              }
              if (keys.length === pageIds.length) setAssetSelectAllMode('page');
              else setAssetSelectAllMode('custom');
            },
            onSelectAll: (selected) => {
              if (!selected) {
                setSelectedRowKeys([]);
                setAssetSelectAllMode('none');
                return;
              }
              const pageIds = getCurrentPageAssetIds();
              Modal.confirm({
                title: '选择全选范围',
                content: `选择“本页”只会勾选当前页 ${pageIds.length} 条；选择“全部资产”将对当前筛选条件下的全部资产生效。`,
                okText: '全选本页',
                cancelText: '全选全部资产',
                onOk: () => {
                  setSelectedRowKeys(pageIds);
                  setAssetSelectAllMode('page');
                },
                onCancel: () => {
                  // 勾选框仍以当前页展示；真正“全部资产”由 assetSelectAllMode 决定
                  setSelectedRowKeys(pageIds);
                  setAssetSelectAllMode('all');
                },
              });
            },
          }}
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current,
            pageSize,
            total,
            onChange: (page, ps) => fetchData(page, ps),
            showSizeChanger: true,
            showTotal: (t, range) => `显示 ${range[0]} 到 ${range[1]} 条，共 ${t} 条`,
          }}
        />
      </div>

      {/* Batch Change Modal */}
      <DraggableModal
        title="一键修改存放地点/使用人"
        open={batchChangeOpen}
        onCancel={() => setBatchChangeOpen(false)}
        onOk={applyBatchChange}
        okText="一键修改"
        okButtonProps={{ danger: false }}
        confirmLoading={batchChangeLoading}
        destroyOnClose
        maskClosable={false}
      >
        <div>
          <div style={{ marginBottom: 12, color: '#666' }}>
            选择修改范围后，将自动把匹配到的资产逐条变更“存放地点”和/或“使用人”。
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8, color: '#666' }}>修改范围</div>
            <Select
              style={{ width: '100%' }}
              value={batchChangeScope}
              onChange={setBatchChangeScope}
              options={[
                { value: 'selected', label: '修改选中资产' },
                { value: 'page', label: '修改本页' },
                { value: 'all', label: '修改全部资产（当前筛选条件）' },
              ]}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8, color: '#666' }}>新存放地点</div>
            <Select
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="label"
              value={batchNewLocationId}
              onChange={setBatchNewLocationId}
              placeholder="不修改请留空"
              options={(Array.isArray(locations) ? locations : []).map(l => ({ value: l.id, label: l.location_name }))}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8, color: '#666' }}>新使用人</div>
            <Select
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="label"
              value={batchNewUserId}
              onChange={setBatchNewUserId}
              placeholder="不修改请留空"
              options={(Array.isArray(assetUsers) ? assetUsers : []).map(u => ({ value: u.id, label: u.username }))}
            />
          </div>
        </div>
      </DraggableModal>

      {/* Create/Edit Modal */}
      <DraggableModal
        title={editing ? '资产详情' : '新建资产'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        width={720}
        okText="确定"
        cancelText="取消"
        okButtonProps={editing && !detailEditMode ? { disabled: true } : undefined}
        footer={editing ? [
          <Button key="cancel" onClick={() => setModalOpen(false)}>关闭</Button>,
          <Button key="edit" onClick={() => setDetailEditMode((v) => !v)}>
            {detailEditMode ? '取消编辑' : '编辑'}
          </Button>,
          <Button key="save" type="primary" onClick={handleSubmit} disabled={!detailEditMode}>
            保存
          </Button>,
        ] : undefined}
      >
        <Spin spinning={photoBusy}>
          <Form form={form} layout="vertical" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
            <Row gutter={16}>
              {editing && (
                <Col span={24}>
                  <div style={{ marginBottom: 12, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontWeight: 600 }}>资产照片（{editingPhotos.length}/5）</div>
                      <Space>
                        <Button
                          size="small"
                          icon={<CameraOutlined />}
                          disabled={!detailEditMode || editingPhotos.length >= ASSET_PHOTO_MAX_COUNT}
                          onClick={() => setPhotoPickerOpen(true)}
                        >
                          添加照片
                        </Button>
                      </Space>
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                      第一张为首图封面；可用左右箭头或「设为封面」调整顺序。手机拍照会自动压缩后上传。
                    </div>
                    <input
                      ref={photoReplaceInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (replacePhotoIdRef.current) {
                          replaceEditingPhoto(replacePhotoIdRef.current, f);
                        }
                        replacePhotoIdRef.current = null;
                      }}
                    />
                    {editingPhotos.length ? (
                      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                        {editingPhotos.map((p, idx) => (
                          <div
                            key={p.id}
                            style={{ width: 108, flex: '0 0 auto' }}
                          >
                            <div style={{ position: 'relative' }}>
                              <img
                                src={p.url}
                                alt="资产照片"
                                style={{
                                  width: '100%',
                                  height: 72,
                                  borderRadius: 6,
                                  objectFit: 'cover',
                                  border: idx === 0 ? '2px solid #1677FF' : '1px solid #f0f0f0',
                                }}
                              />
                              {idx === 0 ? (
                                <Tag color="blue" style={{ position: 'absolute', top: 4, left: 4, margin: 0 }}>
                                  封面
                                </Tag>
                              ) : null}
                            </div>
                            <Space size={4} style={{ marginTop: 4 }} wrap>
                              <Button
                                size="small"
                                icon={<LeftOutlined />}
                                disabled={!detailEditMode || idx === 0}
                                onClick={() => moveEditingPhoto(p.id, -1)}
                              />
                              <Button
                                size="small"
                                disabled={!detailEditMode || idx === 0}
                                onClick={() => makeEditingCover(p.id)}
                              >
                                封面
                              </Button>
                              <Button
                                size="small"
                                icon={<RightOutlined />}
                                disabled={!detailEditMode || idx === editingPhotos.length - 1}
                                onClick={() => moveEditingPhoto(p.id, 1)}
                              />
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                disabled={!detailEditMode}
                                onClick={() => {
                                  replacePhotoIdRef.current = p.id;
                                  photoReplaceInputRef.current?.click();
                                }}
                              />
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={!detailEditMode}
                                onClick={() => deleteEditingPhoto(p.id)}
                              />
                            </Space>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#999' }}>暂无照片</div>
                    )}
                  </div>
                </Col>
              )}
            <Col span={12}>
              <Form.Item name="asset_code" label="资产编号" rules={[{ required: true, message: '请输入资产编号' }]}>
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="check_code" label="清查编号">
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="asset_name" label="资产名称" rules={[{ required: true, message: '请输入资产名称' }]}>
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="asset_type" label="资产类型">
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="quantity" label="数量" initialValue={1}>
                <InputNumber min={1} style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unit_price" label="单价">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="brand" label="品牌">
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="型号">
                <Input disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="original_value" label="原值" extra="填写单价时原值=数量×单价">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accounting_date"
                label="入账日期"
                rules={[{ required: true, message: '请选择入账日期' }]}
              >
                <DatePicker style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="acquire_date" label="取得日期">
                <DatePicker style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="monthly_depreciation" label="月折旧额">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="total_month" label="折旧总月数">
                <InputNumber min={0} style={{ width: '100%' }} disabled={editing && !detailEditMode} />
              </Form.Item>
            </Col>
            {editing && detailSnapshot && (
              <>
                <Col span={12}>
                  <Form.Item label="已摊销月数">
                    <Input value={detailSnapshot.used_month ?? '-'} disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="已摊销金额">
                    <Input
                      value={detailSnapshot.amortized_amount != null
                        ? `¥${Number(detailSnapshot.amortized_amount).toLocaleString()}`
                        : '-'}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="当前净值">
                    <Input
                      value={detailSnapshot.net_value != null
                        ? `¥${Number(detailSnapshot.net_value).toLocaleString()}`
                        : '-'}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="剩余折旧月数">
                    <Input value={detailSnapshot.remaining_months ?? '-'} disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="折旧状态">
                    <Input
                      value={detailSnapshot.status_text
                        || (Number(detailSnapshot.net_value) <= 0 ? '折旧完成' : '折旧中')}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            {editing && detailLoading && (
              <Col span={24}>
                <div style={{ color: '#999', marginBottom: 8 }}>正在加载最新折旧信息…</div>
              </Col>
            )}
            <Col span={12}>
              <Form.Item name="location_id" label="存放位置">
                <Select
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  disabled={editing && !detailEditMode}
                  options={(Array.isArray(locations) ? locations : []).map(l => ({ value: l.id, label: l.location_name }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="user_id" label="使用人">
                <Select
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  disabled={editing && !detailEditMode}
                  options={(Array.isArray(assetUsers) ? assetUsers : []).map(u => ({ value: u.id, label: u.username }))}
                />
              </Form.Item>
            </Col>
            </Row>
          </Form>
        </Spin>
      </DraggableModal>
      <AssetPhotoPicker
        open={photoPickerOpen}
        onClose={() => setPhotoPickerOpen(false)}
        disabled={photoBusy || !detailEditMode}
        onPicked={uploadEditingPhoto}
      />

      {/* Log Modal */}
      <DraggableModal
        title={`操作日志 - ${logAssetName}`}
        open={logModalOpen}
        onCancel={() => setLogModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        {logs.length > 0 ? (
          <Timeline
            style={{ marginTop: 20 }}
            items={logs.map((log) => ({
              children: (
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {log.operate_type || log.action || log.operation || log.type || '操作记录'}
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>{log.detail || log.description || log.content}</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {log.operate_user || log.operator || log.user_name || '-'}
                    {' '}
                    &middot;
                    {' '}
                    {log.create_time || log.created_at || log.time || '-'}
                  </div>
                </div>
              ),
            }))}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无日志记录</div>
        )}
      </DraggableModal>

      <DraggableModal
        title={ioDialog.title || '处理中'}
        open={ioDialog.open}
        closable={!ioDialog.loading}
        maskClosable={false}
        keyboard={!ioDialog.loading}
        footer={ioDialog.loading ? null : [
          <Button key="ok" type="primary" onClick={() => setIoDialog({
            open: false, loading: false, title: '', lines: [],
          })}
          >
            确定
          </Button>,
        ]}
      >
        {ioDialog.loading ? (
          <div style={{ padding: '8px 0', textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12, color: '#666' }}>{ioDialog.lines?.[0] || '处理中，请稍候...'}</div>
          </div>
        ) : (
          <div style={{ lineHeight: 1.9, maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {(ioDialog.lines || []).map((line, idx) => (
              <div
                key={`io-line-${idx}`}
                style={{
                  color: String(line).includes('失败') || String(line).includes('第') ? '#a94442' : undefined,
                }}
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </DraggableModal>

      <DraggableModal
        title="导入导出操作日志"
        open={opLogOpen}
        onCancel={() => setOpLogOpen(false)}
        footer={[
          <Button key="exportSelected" onClick={exportSelectedOpLogs}>导出选中详情</Button>,
          <Button key="close" onClick={() => setOpLogOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <Table
          rowKey="id"
          size="small"
          loading={opLogLoading}
          dataSource={opLogs}
          rowSelection={{ selectedRowKeys: opLogSelected, onChange: setOpLogSelected }}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: '时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
            { title: '类型', dataIndex: 'action', key: 'action', width: 80, render: v => (v === 'import' ? '导入' : '导出') },
            { title: '文件', dataIndex: 'file_name', key: 'file_name', width: 200 },
            { title: '总数', dataIndex: 'total_count', key: 'total_count', width: 70 },
            { title: '新增/成功', dataIndex: 'created_count', key: 'created_count', width: 90 },
            { title: '更新', dataIndex: 'updated_count', key: 'updated_count', width: 70 },
            { title: '跳过', dataIndex: 'skipped_count', key: 'skipped_count', width: 70 },
            { title: '操作人', dataIndex: 'operator_name', key: 'operator_name', width: 90 },
            {
              title: '操作',
              key: 'actions',
              render: (_, r) => <Button type="link" onClick={() => exportOneOpLog(r.id)}>导出详情</Button>,
            },
          ]}
        />
      </DraggableModal>

      <DraggableModal
        title="自定义排序"
        open={sortModalOpen}
        onOk={applySortRules}
        onCancel={() => setSortModalOpen(false)}
        okText="应用排序"
        cancelText="取消"
        width={760}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666' }}>
          拖拽可调整优先级（排序条件1优先级最高），字段不可重复。
        </div>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {draftSortRules.map((rule, idx) => (
            <Space
              key={`draft-sort-${idx}`}
              wrap
              draggable
              onDragStart={() => setDraggingSortIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (draggingSortIndex === null) return;
                const next = [...draftSortRules];
                const [moved] = next.splice(draggingSortIndex, 1);
                next.splice(idx, 0, moved);
                setDraftSortRules(next);
                setDraggingSortIndex(null);
              }}
              onDragEnd={() => setDraggingSortIndex(null)}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: draggingSortIndex === idx ? '1px dashed #1677ff' : '1px solid #f0f0f0',
                borderRadius: 6,
                background: draggingSortIndex === idx ? '#f0f7ff' : '#fff',
              }}
            >
              <HolderOutlined style={{ color: '#999', cursor: 'grab' }} />
              <span style={{ color: '#666' }}>排序条件{idx + 1}</span>
              <Select
                style={{ width: 180 }}
                value={rule.field}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], field: v };
                  setDraftSortRules(next);
                }}
                options={sortFieldOptions.filter(opt => {
                  const used = draftSortRules
                    .map((item, i) => (i === idx ? null : item.field))
                    .filter(Boolean);
                  return !used.includes(opt.value);
                })}
              />
              <Select
                style={{ width: 120 }}
                value={rule.order}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], order: v };
                  setDraftSortRules(next);
                }}
                options={[
                  { value: 'asc', label: '升序' },
                  { value: 'desc', label: '降序' },
                ]}
              />
              <Button
                onClick={() => setDraftSortRules(draftSortRules.filter((_, i) => i !== idx))}
              >
                删除
              </Button>
            </Space>
          ))}
          <Space>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                const used = draftSortRules.map(r => r.field);
                const firstUnused = sortFieldOptions.find(opt => !used.includes(opt.value));
                if (!firstUnused) {
                  message.info('可选排序字段已全部使用');
                  return;
                }
                setDraftSortRules([...draftSortRules, { field: firstUnused.value, order: 'asc' }]);
              }}
              style={{ width: 140 }}
            >
              增加排序条件
            </Button>
            <Button onClick={restoreDefaultSortRules}>
              恢复默认排序
            </Button>
          </Space>
        </Space>
      </DraggableModal>

      <DraggableModal
        title="按筛选条件删除（需重新填写）"
        open={deleteFilterModalOpen}
        onOk={handleDeleteFiltered}
        onCancel={() => setDeleteFilterModalOpen(false)}
        okText="确认删除"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#cf1322' }}>
          为避免误删，必须重新填写筛选条件；禁止空白条件删除。
        </div>
        <Form form={deleteFilterForm} layout="vertical">
          <Form.Item label="关键词" name="keyword">
            <Input placeholder="资产名称/编号/清查编号" allowClear />
          </Form.Item>
          <Form.Item label="类别" name="asset_type">
            <Select placeholder="请选择类别" allowClear options={categoryOptions} />
          </Form.Item>
          <Form.Item label="折旧状态" name="asset_status">
            <Select
              placeholder="请选择折旧状态"
              allowClear
              options={[
                { value: 0, label: '折旧中' },
                { value: 1, label: '已完成' },
              ]}
            />
          </Form.Item>
          <Form.Item label="存放位置" name="location_ids">
            <Select
              placeholder="请选择存放位置"
              allowClear
              showSearch
              optionFilterProp="label"
              mode="multiple"
              options={(Array.isArray(locations) ? locations : []).map(l => ({ value: l.id, label: l.location_name }))}
            />
          </Form.Item>
          <Form.Item label="使用人" name="user_ids">
            <Select
              placeholder="请选择使用人"
              allowClear
              showSearch
              optionFilterProp="label"
              mode="multiple"
              options={(Array.isArray(assetUsers) ? assetUsers : []).map(u => ({ value: u.id, label: u.username }))}
            />
          </Form.Item>
          <Form.Item label="净值区间">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="net_value_min" noStyle>
                <InputNumber placeholder="最小净值" min={0} style={{ width: '50%' }} />
              </Form.Item>
              <Form.Item name="net_value_max" noStyle>
                <InputNumber placeholder="最大净值" min={0} style={{ width: '50%' }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Form>
      </DraggableModal>
    </div>
  );
}
